import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chips,
  DataTable,
  EmptyState,
  Icon,
  Input,
  Panel,
  Select,
  Stat,
  useErrorDialog,
} from '@dayon/design-system';
import { formatCount, formatPesosCompact, formatRate } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';

import { affiliateColumns, affiliatePeriodColumns, type AffiliatePeriodRow } from './columns.tsx';
import { PayoutRunDialog } from '../payouts/PayoutRunDialog.tsx';
import { AffiliateDetailDrawer } from './AffiliateDetailDrawer.tsx';
import { CommissionModelsDrawer } from './CommissionModelsDrawer.tsx';
import { ApproveAffiliateDialog } from './ApproveAffiliateDialog.tsx';
import { NewAffiliateDialog } from './NewAffiliateDialog.tsx';
import { PortalAccessDialog } from './PortalAccessDialog.tsx';
import { CommissionModelDialog } from './CommissionModelDialog.tsx';
import { QualityReviewDialog } from './QualityReviewDialog.tsx';
import {
  CHARGEBACK_CEILING_BP,
  FALLBACK_FTD_FLOOR_BP,
  SORTS,
  STATUS_FILTERS,
  type AffiliateListPayload,
  type AffiliateRow,
  type PlanRow,
  type SortKey,
  type StatusFilter,
  type Thresholds,
  type MeasuredWindow,
} from './types.ts';

/**
 * §5 Affiliate management — the marketing-partner ledger.
 *
 * Split across this directory rather than one 46KB file, by what each piece
 * does: `types.ts` (shapes and vocabulary), `columns.tsx` (the two tables),
 * a drawer per surface and a dialog per decision. This file composes them and
 * owns the reads.
 *
 * ── WHAT THIS SCREEN USED TO REFUSE, AND NOW DOES ──────────────────────────
 *
 * It carried a "Not yet available" panel with six entries. Five are gone
 * entirely and the sixth has shrunk to one half of itself, because the thing
 * each described was built:
 *
 *   30-day windows        Every traffic column is now the 30 gaming days
 *                         `gamingDayWindow` defines, with the lifetime total
 *                         underneath it. The labels finally measure what they
 *                         say.
 *   Accrued versus paid   Three figures from three tables — accrued (a period
 *                         result whose period is not PAID), paid
 *                         (affiliate_payout) and clawed back
 *                         (affiliate_clawback). "Unpaid" is now true.
 *   Tracking links        GET /api/admin/affiliates/:id/detail returns them,
 *                         and the list returns platform.brand.domain, so the
 *                         drawer copies a whole working link.
 *   The quality trigger   `affiliate.quality-review` in the worker opens one
 *                         when 30-day FTD conversion falls under the floor,
 *                         and the floor is READ from platform.setting through
 *                         a route scoped to two keys and this screen's own
 *                         capability rather than to access.manage.
 *   Every write           Filing, approving, setting a commission model and
 *                         closing a review, each auditing under subject types
 *                         `affiliate` and `affiliate_plan` that the trail had
 *                         never carried.
 *   Player quality        Three of its four figures — 30-day retention,
 *                         average first deposit and the bonus-only share —
 *                         are measured in the drawer, each printed with the
 *                         denominator it is a share OF.
 *
 * The fourth of those four is what is left, and the note below says exactly
 * what is missing: CHARGEBACK RATE, and with it the chargeback half of the
 * auto-review rule. No payment provider sends chargebacks or disputes to the
 * platform. Staff can record one by hand — a ledger reversal with the reason
 * PROVIDER_CHARGEBACK, or an adjustment or restriction coded CHARGEBACK — but
 * nothing turns those into a per-affiliate rate, so it is not evaluated by
 * anything — not here, not in the worker — and no percentage is invented for it.
 */

/** Which capability decides a write here. See the note beside the writes. */
const WRITE_PERMISSION = 'partners.manage' as const;

export function Affiliates() {
  const { brandId, brands, session, allowed } = useSession();
  /**
   * The capability this module needs, read once and used in BOTH places that
   * need it: the refusal that decides what is painted, and the effects that
   * decide what is fetched. An early return covers only the first.
   */
  const canReadPartners = allowed('partners.view');
  const { showError } = useErrorDialog();
  /* Settlement is its own gate and must stay its own gate: the periods route
     guards on `funds.approve`, not on the `partners.manage` the writes use. */
  const canSeeSettlement = allowed('funds.approve');

  const [rows, setRows] = useState<AffiliateRow[]>([]);
  /* Named `measured`, not `window`: a state variable called window shadows the
     global inside this component, and the next person to reach for
     window.matchMedia or window.location here would get a KPI window instead. */
  const [measured, setMeasured] = useState<MeasuredWindow | null>(null);
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [plansLoading, setPlansLoading] = useState(true);
  /*
   * Did the last read FAIL, or did it succeed and return nothing?
   *
   * The error modal cannot carry that distinction on its own. It is dismissed
   * in a second, and what stands behind it is the copy written for a genuinely
   * empty network — "there is genuinely no partner sending traffic yet", the
   * last branch of the table's empty state, which is true when the read of
   * `affiliate.affiliate` within the brand scope came back with no rows — over a
   * KPI band of zeroes. "This network earned nothing" and "we could not ask what
   * it earned" are different facts about the same ledger, and only one of them is
   * safe to act on. The screen says which, in words rather than by naming the
   * table it asked.
   *
   * Three flags because three independent GETs back this screen: the rate card
   * can be healthy while the ledger is down, and a single shared flag would
   * print "could not be read" over a plans list that arrived intact.
   */
  const [loadFailed, setLoadFailed] = useState(false);
  const [plansFailed, setPlansFailed] = useState(false);
  const [thresholds, setThresholds] = useState<Thresholds | null>(null);

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<StatusFilter>('ALL');
  const [sort, setSort] = useState<SortKey>('DEFAULT');
  const [descending, setDescending] = useState(true);
  const [detail, setDetail] = useState<AffiliateRow | null>(null);
  const [plansOpen, setPlansOpen] = useState(false);
  const [approving, setApproving] = useState<AffiliateRow | null>(null);
  const [reviewing, setReviewing] = useState<AffiliateRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  /* The affiliate whose PORTAL LOGIN is being looked at. Its own state rather
     than a mode on `detail`: the dialog sits over the drawer, and closing one
     must not close the other. */
  const [portalFor, setPortalFor] = useState<AffiliateRow | null>(null);
  const [planFor, setPlanFor] = useState<AffiliateRow | null>(null);
  const [planOpen, setPlanOpen] = useState(false);
  /* Bumped by every successful write, and a dependency of the two reads. A
     write moves the KPI band and the rate card as well as one row, so the
     screen re-reads rather than patching the row it happens to know about. */
  const [writes, setWrites] = useState(0);
  /** Platform-wide affiliate settlement periods, and the run that is open. */
  const [periods, setPeriods] = useState<AffiliatePeriodRow[]>([]);
  const [periodsFailed, setPeriodsFailed] = useState(false);
  const [runPeriodId, setRunPeriodId] = useState<string | null>(null);

  const reload = useCallback(() => setWrites((n) => n + 1), []);

  // brandId is a real scope here: the affiliates handler runs brandScope() and
  // narrows on affiliate.brand_id. The plans handler does not — see the notice.
  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canReadPartners) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (brandId) params.set('brandId', brandId);
    const suffix = params.toString() ? `?${params}` : '';

    api
      .get<AffiliateListPayload>(`/api/admin/affiliates${suffix}`)
      .then((payload) => {
        if (cancelled) return;
        setRows(payload.items);
        setMeasured(payload.window);
        setLoadFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Emptied deliberately: the previous scope's ledger left sitting under a
        // failure notice would be read as this scope's answer.
        setRows([]);
        setLoadFailed(true);
        showError({
          title: 'Could not load the affiliate network',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The affiliate ledger could not be fetched. Try again shortly.',
        });
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [canReadPartners, brandId, showError, writes]);

  /*
   * Settlement is its own read, and its own GATE.
   *
   * `/api/admin/affiliates/periods` guards on `funds.approve` — it reads when
   * money moves — while the partner writes on this screen guard on
   * `partners.manage`. Both are held by the same two roles today, so nothing
   * changes for anyone now; asking for it without the capability would 403 and
   * pop an error dialog for a panel the reader never asked to see.
   */
  useEffect(() => {
    if (!canSeeSettlement) return;
    let cancelled = false;

    api
      .get<{ items: AffiliatePeriodRow[] }>('/api/admin/affiliates/periods')
      .then((payload) => {
        if (cancelled) return;
        setPeriods(payload.items);
        setPeriodsFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Emptied, and FLAGGED. "No period exists" and "we could not ask" are
        // different facts about settlement and only one is safe to act on.
        setPeriods([]);
        setPeriodsFailed(true);
        showError({
          title: 'Could not load the affiliate commission periods',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The settlement periods could not be fetched. Try again shortly.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [canSeeSettlement, showError, writes]);

  /* The rate card is its own read, and platform-wide —
     /api/admin/affiliates/plans takes no brandId, as the drawer's own notice
     says. It is fetched separately from the ledger so that each knows its own
     outcome: one Promise.all with one catch could only report that "something"
     failed, and that verdict would then be printed over whichever of the two
     actually arrived. */
  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canReadPartners) {
      setPlansLoading(false);
      return;
    }
    let cancelled = false;
    setPlansLoading(true);

    api
      .get<{ items: PlanRow[] }>('/api/admin/affiliates/plans')
      .then((plan) => {
        if (cancelled) return;
        setPlans(plan.items);
        setPlansFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setPlans([]);
        setPlansFailed(true);
        showError({
          title: 'Could not load the affiliate commission models',
          message:
            error instanceof ApiRequestError
              ? error.message
              : 'The commission rate card could not be fetched. Try again shortly.',
        });
      })
      .finally(() => !cancelled && setPlansLoading(false));

    return () => {
      cancelled = true;
    };
  }, [canReadPartners, showError, writes]);

  /*
   * The configured thresholds.
   *
   * Deliberately NOT behind the error dialog. This read decides whether a
   * number on screen is the configured one or the fallback, and the screen says
   * which — a modal on top of that would report a failure the user cannot act
   * on and cannot make the figure any more or less true.
   */
  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canReadPartners) {
      return;
    }
    let cancelled = false;
    api
      .get<Thresholds>('/api/admin/affiliates/thresholds')
      .then((t) => !cancelled && setThresholds(t))
      .catch(() => !cancelled && setThresholds(null));
    return () => {
      cancelled = true;
    };
  }, [canReadPartners]);

  const configuredFloor = thresholds?.items['affiliate.quality_review_ftd_pct']?.value;
  /* Percent in the row, basis points everywhere else — the same conversion the
     worker does, so the screen and the trigger cannot disagree about the line. */
  const floorBp =
    configuredFloor === undefined
      ? FALLBACK_FTD_FLOOR_BP
      : Math.round(Number(configuredFloor) * 100);
  const floorIsConfigured =
    configuredFloor !== undefined && Number.isFinite(Number(configuredFloor));
  const attributionDays = thresholds?.items['affiliate.attribution_window_days']?.value ?? null;

  /**
   * The rows the SEARCH leaves standing, before the status chips run.
   *
   * Split out so the chip badges can be counted over it. A badge is a promise
   * about how many rows its chip will produce: counting the whole loaded list
   * would print "Active 3" directly above the one active affiliate a search had
   * left, which is the same lie the risk queue's badges used to tell.
   */
  const searched = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return rows;
    // Code + name + traffic source, concatenated, case-insensitive.
    return rows.filter((a) => `${a.ref} ${a.name} ${a.source_kind}`.toLowerCase().includes(needle));
  }, [rows, query]);

  const counts = useMemo(() => {
    const by: Record<StatusFilter, number> = {
      ALL: searched.length,
      ACTIVE: 0,
      PENDING: 0,
      REVIEW: 0,
      SUSPENDED: 0,
    };
    for (const a of searched) {
      if (a.status === 'ACTIVE') by.ACTIVE += 1;
      else if (a.status === 'PENDING') by.PENDING += 1;
      else if (a.status === 'REVIEW') by.REVIEW += 1;
      else if (a.status === 'SUSPENDED') by.SUSPENDED += 1;
    }
    return by;
  }, [searched]);

  /**
   * The KPI band, computed over the whole loaded brand scope — deliberately NOT
   * over the search or the chips. These four describe the network the header
   * counts; narrowing them with a text box would make "Accrued commission" mean
   * something different on every keystroke.
   */
  const totals = useMemo(() => {
    let signups = 0;
    let ftd = 0;
    let accrued = 0n;
    let paid = 0n;
    let awaiting = 0;
    for (const a of rows) {
      signups += Number(a.signups_30d);
      ftd += Number(a.ftd_30d);
      accrued += BigInt(a.accrued_commission);
      paid += BigInt(a.paid_commission);
      // "Pending review counts both Pending (new, never approved) and Review
      // (quality review)" — two different reasons, one queue for the operator.
      if (a.status === 'PENDING' || a.status === 'REVIEW') awaiting += 1;
    }
    return {
      signups,
      ftd,
      accrued,
      paid,
      // Measured, not asserted: no signups means no conversion rate, not 0%.
      conversionBp: signups === 0 ? null : Math.round((ftd * 10000) / signups),
      awaiting,
    };
  }, [rows]);

  const visible = useMemo(() => {
    const matched = filter === 'ALL' ? searched : searched.filter((a) => a.status === filter);

    // DEFAULT keeps the server's ordering, which is a triage order — Pending,
    // then Review, then Suspended, then Active by accrued commission, with the
    // ref as the unique tiebreaker. Re-sorting by default would bury the rows
    // that need a person.
    if (sort === 'DEFAULT') return matched;

    const dir = descending ? -1 : 1;
    return matched.toSorted((a, b) => {
      switch (sort) {
        case 'NAME':
          return dir * a.name.localeCompare(b.name, 'en-PH');
        case 'SIGNUPS':
          return dir * (Number(a.signups_30d) - Number(b.signups_30d));
        case 'FTD':
          return dir * (Number(a.ftd_30d) - Number(b.ftd_30d));
        default: {
          // Commission is centavos and can exceed Number's safe range once a
          // network scales, so it is compared as bigint and mapped to a sign.
          const delta = BigInt(a.accrued_commission) - BigInt(b.accrued_commission);
          return dir * (delta > 0n ? 1 : delta < 0n ? -1 : 0);
        }
      }
    });
  }, [searched, filter, sort, descending]);

  /**
   * The endpoint gate is `partners.view`, and the sidebar entry uses the same
   * key. Gating the whole screen rather than a button: without it the API
   * returns 403 and there is nothing to render.
   */
  if (!canReadPartners) {
    return (
      <EmptyState icon="lock-key" title="Affiliate management">
        This module needs the partners.view capability. Your role, {session?.role ?? 'unknown'},
        does not hold it.
      </EmptyState>
    );
  }

  /*
   * Reading and deciding are different capabilities.
   *
   * Every read on this screen is `partners.view`. Every WRITE is
   * `partners.manage` — a write capability carrying mandatory two-factor, held by
   * Super Admin and Finance Lead, and already the gate on the partner
   * settlement view next door. Gating a write on the view permission is the
   * defect that once let a read-only Auditor post into a customer's support
   * thread, so it is not done here.
   *
   * "Permission-gated actions are HIDDEN, not disabled": a Risk Officer or an
   * Auditor reads this whole screen and is shown no decision buttons at all,
   * rather than a row of controls that answer 403.
   */
  const canWrite = allowed(WRITE_PERMISSION);
  const brandName = brands.find((b) => b.id === brandId)?.name;
  const windowLabel = measured === null ? '30 days' : `${measured.days} days`;

  return (
    <>
      {/*
        The purpose belongs HERE and not in the notice below.

        The quiet notice under this header already carries two rules an operator
        must hold in mind while reading the table — what attribution promises,
        and what suspending does not do. Adding "what this screen is for" as a
        third stacked paragraph would have buried both: three prose blocks in a
        column are read as one wall and skipped as one wall. The header is the
        one place a sentence about the screen can sit without competing with a
        rule about the data.
      */}
      <ScreenHeader
        title="Affiliate management"
        purpose="Where the partner and finance team decide who may send you players, what each one is paid, and what commission is still owed."
        /*
          The caption kept exactly as it was, both halves of it.

          It is tri-state on purpose and the three states are not
          interchangeable: "Loading…" means the answer has not arrived yet,
          "could not be read" means the request failed and the figures beside it
          were never summed, and the count means the ledger answered. Collapsing
          the middle state into a zero count is the specific mistake this wording
          exists to prevent — see the long note beside `loadFailed` above.

          The brand-scope suffix stays a SEPARATE expression appended to
          whichever state won, because it is true of all three: a failed read
          failed within a scope, and a reader who does not know whether they are
          looking at one brand or all of them cannot judge the number either way.
        */
        meta={
          <>
            {loading && rows.length === 0
              ? 'Loading…'
              : loadFailed
                ? 'the affiliate ledger could not be read'
                : `${formatCount(rows.length)} affiliates · ${
                    attributionDays === null
                      ? 'attribution window NOT CONFIGURED'
                      : `last-click attribution, ${attributionDays}-day window`
                  }`}
            {brandId ? ' · brand-scoped' : ' · all brands'}
          </>
        }
        actions={
          <>
            <Button onClick={() => setPlansOpen(true)}>
              <Icon name="receipt" />
              Commission models
            </Button>
            {/* Hidden rather than disabled for a reader without the write
                capability, which is the rule stated in the note above `canWrite`. */}
            {canWrite && (
              <Button variant="primary" onClick={() => setCreateOpen(true)}>
                <Icon name="plus" />
                New affiliate
              </Button>
            )}
          </>
        }
      />

      {/* The two rules an operator has to hold in mind while reading the table:
          what attribution actually promises, and what suspending does not do. */}
      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          {/*
            THE WINDOW IS THE SETTING, OR IT IS NOTHING.

            This used to fall back to "30" when the row could not be read, which
            was harmless while nothing implemented attribution at all. It is not
            harmless now: `services/api/src/modules/attribution` reads
            `platform.setting['affiliate.attribution_window_days']` on every
            click and every signup, and REFUSES to attribute anybody when it is
            missing or unusable. A screen printing "30-day cookie window" over a
            platform that is attributing nobody would be the most expensive kind
            of wrong — an operator would go looking for the missing partners
            everywhere except the setting.
          */}
          {attributionDays === null ? (
            <>
              <strong>The attribution window is not configured.</strong> Attribution is last-click,
              and the window comes from the attribution window — with no
              readable value the platform attributes <strong>nobody</strong>: clicks are still
              redirected, but no signup is credited to any affiliate until the setting is restored.
            </>
          ) : (
            <>
              Attribution is{' '}
              <strong>last-click with a {attributionDays}-day cookie window</strong>, resolved to
              one affiliate per player.
            </>
          )}{' '}
          Commission models are per-affiliate and dated:
          changing one affects <strong>future cohorts only</strong> — players already attributed
          keep the plan they were acquired under. Suspending stops new attribution but existing
          players keep earning revshare unless it is clawed back.
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="stat-grid">
          {Array.from({ length: 4 }, (_, i) => (
            <div className="stat" key={i}>
              <div className="skeleton" style={{ height: '11px', width: '60%' }}>
                &nbsp;
              </div>
              <div className="skeleton" style={{ height: '25px', width: '80%' }}>
                &nbsp;
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="stat-grid">
          {/* Every tile here is summed from rows. A read that failed has no rows
              to sum, and a band of zeroes over it would tell an operator the
              network earned nothing — so each tile says it was not summed. */}
          <Stat
            label="Affiliate signups"
            value={loadFailed ? '—' : formatCount(totals.signups)}
            delta={
              loadFailed
                ? 'not summed — the affiliate read failed'
                : `${formatCount(rows.length)} affiliates · last ${windowLabel}`
            }
          />
          <Stat
            label="First-time depositors"
            value={loadFailed ? '—' : formatCount(totals.ftd)}
            delta={
              loadFailed
                ? 'not summed — the affiliate read failed'
                : totals.conversionBp === null
                  ? 'no attributed signups to convert'
                  : `${formatRate(totals.conversionBp)} conversion · last ${windowLabel}`
            }
          />
          <Stat
            label="Commission unpaid"
            value={loadFailed ? '—' : formatPesosCompact(totals.accrued)}
            // It can finally be called unpaid: accrued sums only period results
            // whose period has NOT reached PAID, and what has been settled is
            // named beside it rather than folded in.
            delta={
              loadFailed
                ? 'not summed — the affiliate read failed'
                : `${formatPesosCompact(totals.paid)} already paid`
            }
          />
          <Stat
            label="Pending review"
            value={loadFailed ? '—' : totals.awaiting}
            deltaUp={!loadFailed && totals.awaiting > 0}
            delta={
              loadFailed
                ? 'not counted — the affiliate read failed'
                : totals.awaiting > 0
                  ? 'new or quality review'
                  : 'none waiting'
            }
          />
        </div>
      )}

      <Panel>
        <div className="panel-head">
          <h3 className="panel-title">Affiliates</h3>
          <div className="header-spacer">
            <Chips
              label="Filter affiliates by status"
              active={filter}
              onChange={setFilter}
              /* A badge is a promise about how many rows its chip will
                 produce. With nothing read there is nothing to promise, and a
                 row of zeroes would say every status is empty — so the counts
                 are dropped rather than set to nothing. */
              options={STATUS_FILTERS.map((f) =>
                loadFailed
                  ? { key: f.key, label: f.label }
                  : { key: f.key, label: f.label, count: counts[f.key] },
              )}
            />
          </div>
        </div>

        <div className="row">
          <div className="search-field" style={{ flex: '1 1 260px', maxWidth: '420px' }}>
            <Icon name="magnifying-glass" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Affiliate name, code or traffic source"
              aria-label="Affiliate name, code or traffic source"
            />
          </div>
          <Select
            aria-label="Sort affiliates by"
            value={sort}
            onChange={(e) => {
              const next = e.target.value as SortKey;
              setSort(next);
              // A name sorts A–Z first, a figure sorts biggest first. That is
              // what an operator means by "sort by this", and the toggle is
              // still there for the other direction.
              setDescending(next !== 'NAME');
            }}
            style={{ width: 'auto' }}
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key}>
                {s.label}
              </option>
            ))}
          </Select>
          <Button
            iconOnly
            aria-label={descending ? 'Sort ascending' : 'Sort descending'}
            // The server order is fixed, so a direction toggle over it would
            // claim to do something it does not.
            disabled={sort === 'DEFAULT'}
            onClick={() => setDescending((d) => !d)}
          >
            <Icon name={descending ? 'arrow-down' : 'arrow-up'} />
          </Button>
        </div>

        {loading && rows.length === 0 ? (
          <div className="skeleton" style={{ height: '240px' }}>
            &nbsp;
          </div>
        ) : (
          <DataTable
            caption="Affiliates with traffic source, commission model, status, 30-day signups, first-time depositors, earnings per click and unpaid commission. Select a row for the affiliate detail."
            columns={affiliateColumns(
              sort,
              descending,
              floorBp,
              canWrite,
              setApproving,
              setReviewing,
            )}
            rows={visible}
            rowKey={(a) => a.id}
            onRowClick={setDetail}
            empty={
              <EmptyState
                icon={loadFailed ? 'warning-circle' : 'megaphone'}
                title={
                  loadFailed ? 'The affiliate ledger could not be read' : 'No affiliate matches'
                }
              >
                {/* The failed read answers FIRST, and it never answers with a
                    zero: what is on screen is the absence of an answer, not an
                    answer of none.

                    THE LAST BRANCH USED TO NAME THE TABLE. It read
                    "affiliate.affiliate holds no rows in this brand scope",
                    which is where the screen learned it — the list read is
                    `affiliate.affiliate` narrowed by `brand_id` — and an
                    operator can do nothing with that name: it is not something
                    they can look up, ask for, or repeat to anyone. The CLAIM it
                    was making is the valuable part and is kept word for word in
                    substance: the read succeeded, nothing was filtered out, and
                    the emptiness is a real fact about the network rather than a
                    failure to ask. That is the one distinction an operator most
                    needs from an empty table, and it is the only reason these
                    three branches are three branches.

                    THE FIRST BRANCH NAMED THE ROUTE for the same reason — it
                    read "/api/admin/affiliates did not come back", and the
                    request it means is `GET /api/admin/affiliates`. An operator
                    cannot retry a path or quote one to anybody useful, and the
                    sentence loses nothing by saying which READ failed in the
                    words the screen already uses for it. */}
                {loadFailed
                  ? 'The request for the affiliate list did not come back, so this table is empty because nothing was read — not because no partner is sending traffic. The message that just popped up carries what went wrong; try again shortly.'
                  : query || filter !== 'ALL'
                    ? 'Nothing matches this filter. Search covers the affiliate code, name and traffic source; the status chips are mutually exclusive, so only one status is in view.'
                    : 'No affiliate is recorded in the brands you are viewing. The list was read successfully and nothing was filtered out — there is genuinely no partner sending traffic yet.'}
              </EmptyState>
            }
          />
        )}

        <div className="pagination">
          {loadFailed
            ? 'No affiliate rows were read — the ledger request failed'
            : `${formatCount(visible.length)} of ${formatCount(rows.length)} affiliates`}
        </div>
      </Panel>

      {/* ── Settlement ────────────────────────────────────────────────────
          The affiliate twin of the agent screen's periods panel, and the screen
          a payout run is raised from. Neither existed: `affiliate_period` had
          one writer (the seed), and an affiliate could request an early payout
          that nobody in the back office could answer. */}
      {canSeeSettlement && (
        <Panel title="Commission periods">
          {periodsFailed ? (
            <EmptyState icon="warning-circle" title="The commission periods could not be read">
              The commission periods could not be fetched, so this panel
              is empty because nothing was read — not because no period exists. It is its own
              request, so the rest of this screen says nothing about it either way.
            </EmptyState>
          ) : periods.length === 0 ? (
            <EmptyState icon="calendar-blank" title="No affiliate periods yet">
              {/* The worker's affiliate.commission.open job cuts them. */}
              Nothing has been opened in the commission periods, so no commission has a window and
              no payout has a due date. A background job opens them on the Asia/Manila gaming-day
              boundary.
            </EmptyState>
          ) : (
            <>
              <DataTable
                caption="Affiliate commission periods with status, affiliate count, net revenue, commission, CPA and the payout run"
                columns={affiliatePeriodColumns(setRunPeriodId)}
                rows={periods}
                rowKey={(p) => p.id}
              />
              <div className="text-subtle" style={{ marginTop: 'var(--space-3)' }}>
                Platform-wide and <strong>not</strong> brand-scoped — the periods are read for every
                brand at once, so these totals do not move with the header&rsquo;s brand selector. A run raised from
                here creates one scheduled payout per payable affiliate{' '}
                <em>within your own brand scope</em>, each needing a second operator to approve it.
              </div>
            </>
          )}
        </Panel>
      )}

      {/* The step the worker deliberately stops before: affiliate.commission.close
          never marks a period PAID, because a payment needs an approver and a
          job is nobody. */}
      <PayoutRunDialog
        domain="affiliates"
        periodId={runPeriodId}
        onClose={() => setRunPeriodId(null)}
        onDone={reload}
      />

      {/* The house pattern for a gap: name the section, give the reason. One
          entry left, and it is the one with no data source in the schema. */}
      <Panel title="Not yet available">
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              <strong>Chargeback rate</strong> — the auto-review rule has two halves and
              only one can be evaluated. FTD conversion is measured over {windowLabel} and opens a
              review below{' '}
              {floorIsConfigured ? (
                <>
                  the configured {formatRate(floorBp)} floor
                </>
              ) : (
                <>
                  {formatRate(floorBp)} — the fallback, because the configured threshold could
                  not be fetched, so this figure is not the one actually in force
                </>
              )}
              . The <em>chargebacks above {formatRate(CHARGEBACK_CEILING_BP)}</em> half is{' '}
              <strong>not evaluated by anything</strong>: no payment provider reports chargebacks
              or disputes to the platform, and nothing turns the chargebacks staff record by hand
              into a rate, so no rate is computed here or anywhere else. A per-affiliate
              chargeback percentage would be invented, and it is the number that decides whether a
              partner keeps its plan.
            </div>
          </div>
        </div>
      </Panel>

      <AffiliateDetailDrawer
        affiliate={detail}
        floorBp={floorBp}
        canWrite={canWrite}
        onClose={() => setDetail(null)}
        onChangePlan={(row) => {
          setPlanFor(row);
          setPlanOpen(true);
        }}
        onCloseReview={setReviewing}
        onPortalAccess={setPortalFor}
      />

      <CommissionModelsDrawer
        open={plansOpen}
        onClose={() => setPlansOpen(false)}
        plans={plans}
        loading={plansLoading}
        failed={plansFailed}
        affiliateCount={rows.length}
        ledgerFailed={loadFailed}
        brandLabel={brandId ? `${brandName ?? brandId} list` : null}
        canWrite={canWrite}
        onNewModel={() => {
          setPlanFor(null);
          setPlanOpen(true);
        }}
      />

      <ApproveAffiliateDialog
        affiliate={approving}
        onClose={() => setApproving(null)}
        onDone={reload}
      />

      <QualityReviewDialog
        affiliate={reviewing}
        floorBp={floorBp}
        floorIsConfigured={floorIsConfigured}
        onClose={() => setReviewing(null)}
        onDone={reload}
      />

      {/* Keyed so that opening it for a different affiliate — or for none —
          starts from empty state rather than the previous partner's terms. */}
      <CommissionModelDialog
        key={planFor?.id ?? 'any'}
        open={planOpen}
        onClose={() => {
          setPlanOpen(false);
          setPlanFor(null);
        }}
        onDone={reload}
        affiliates={rows}
        preselected={planFor}
      />

      {/* Keyed so re-opening it for a different affiliate starts from that
          affiliate's state rather than the previous one's, and so a link shown
          once is never shown again beside the wrong partner. */}
      <PortalAccessDialog
        key={portalFor?.id ?? 'none'}
        affiliate={portalFor}
        canWrite={canWrite}
        onClose={() => setPortalFor(null)}
        onDone={reload}
      />

      <NewAffiliateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onDone={reload}
        brands={brands}
        defaultBrandId={brandId}
      />
    </>
  );
}
